import React from 'react';
import { Card, CardContent } from '../components/shadcn/card';
import { Separator } from '../components/shadcn/separator';
import LegalPageLayout from '../components/LegalPageLayout';

const CookiePolicy: React.FC = () => {
  return (
    <LegalPageLayout
      badge="Legal"
      title="Cookie Policy"
      subtitle="How we use cookies and similar tracking technologies"
      effectiveDate="1 January 2026"
      lastUpdated="20 March 2026"
      currentPage="cookies"
    >
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">1. Introduction</h2>
        <p>
          This Cookies Policy explains how Nucleus Systems ("we", "us", or "our") uses cookies and
          similar tracking technologies on our software-as-a-service platform and website located at{' '}
          <a href="https://www.nucleus-systems.com/" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
            https://www.nucleus-systems.com/
          </a>{' '}
          (the "Site").
        </p>
        <p className="mt-4">
          This Policy is intended to be read alongside our Privacy Policy and forms part of our
          commitment to transparent data practices under the EU General Data Protection Regulation
          (GDPR) and the Protection of Personal Information Act 4 of 2013 (POPIA) of South Africa.
        </p>
        <p className="mt-4">
          By continuing to use our Site, you acknowledge that you have read and understood this Policy.
          Where your consent is required, we will explicitly request it via our cookie consent
          mechanism.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">2. What Are Cookies?</h2>
        <p>
          Cookies are small text files stored on your device when you visit a website or use a web
          application. They allow the Site to recognise your device and remember information about your
          visit or session.
        </p>
        <p className="mt-4">Similar technologies include:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">Web beacons / pixel tags:</strong> Tiny image files embedded in pages or emails that track user activity.</li>
          <li><strong className="text-text">Local storage:</strong> Browser-based storage used to retain app state between sessions.</li>
          <li><strong className="text-text">Session storage:</strong> Temporary browser-based storage cleared when a tab or window is closed.</li>
          <li><strong className="text-text">Device fingerprinting:</strong> Collecting device attributes to create a unique identifier.</li>
        </ul>
        <p className="mt-4">
          For simplicity, all of these technologies are referred to collectively as "cookies" throughout
          this Policy.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">3. Categories of Cookies We Use</h2>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.1 Strictly Necessary Cookies</h3>
        <p>
          These cookies are essential for the security and operation of our Site. They do not store
          personally identifiable information for marketing purposes and do not require your consent
          under GDPR or POPIA.
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">__cf_bm:</strong> Set by Cloudflare to detect and mitigate automated bot traffic, protecting the Platform from abuse and security threats.</li>
          <li><strong className="text-text">_cfuvid:</strong> Set by Cloudflare to apply rate limiting rules and distinguish individual users sharing an IP address.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.2 Performance & Analytics Cookies</h3>
        <p>
          These cookies help us understand how users interact with our Platform so we can measure and
          improve the product experience. Data collected is used in aggregate form.
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">AMP_0c48a381e1:</strong> Set by Amplitude to track user interactions and feature usage within the Platform.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.3 Customer Support & Chat Cookies</h3>
        <p>
          Our Site may use third-party tools to provide real-time support. These tools may set their own
          cookies to manage chat sessions and support history. Examples include Intercom, Crisp,
          Zendesk, and Freshdesk.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">4. Cookie Reference Table</h2>
        <p>
          Below is a summary of the principal cookies used on our Site. This list is updated
          periodically and may not include all cookies set by third parties.
        </p>
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm text-left">
            <thead className="bg-surface2 text-text">
              <tr>
                <th className="px-3 py-2">Cookie Name</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="px-3 py-2 font-mono text-xs">__cf_bm</td>
                <td className="px-3 py-2">Strictly Necessary</td>
                <td className="px-3 py-2">Cloudflare</td>
                <td className="px-3 py-2">~30 min</td>
                <td className="px-3 py-2">Identifies and mitigates automated bot traffic to protect the Platform from abuse and security threats.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-mono text-xs">_cfuvid</td>
                <td className="px-3 py-2">Strictly Necessary</td>
                <td className="px-3 py-2">Cloudflare</td>
                <td className="px-3 py-2">Session</td>
                <td className="px-3 py-2">Applies rate limiting rules and distinguishes individual users sharing the same IP address.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-mono text-xs">AMP_0c48a381e1</td>
                <td className="px-3 py-2">Analytics</td>
                <td className="px-3 py-2">Amplitude</td>
                <td className="px-3 py-2">Session</td>
                <td className="px-3 py-2">Tracks user interactions and feature usage within the Platform to measure and improve the product experience.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">5. Legal Basis for Processing</h2>
        <ul className="list-disc list-inside space-y-2">
          <li><strong className="text-text">Consent</strong> (GDPR Art. 6(1)(a) / POPIA s. 11(1)(a)): For analytics cookies (Amplitude). You may withdraw consent at any time.</li>
          <li><strong className="text-text">Legitimate Interests</strong> (GDPR Art. 6(1)(f) / POPIA s. 11(1)(f)): For security and bot-protection cookies (Cloudflare) where our interests do not override your rights.</li>
          <li><strong className="text-text">Contractual Necessity</strong> (GDPR Art. 6(1)(b) / POPIA s. 11(1)(b)): For strictly necessary cookies required to deliver the SaaS service you have subscribed to.</li>
        </ul>
        <Card className="border-border/40 bg-surface2 mt-4">
          <CardContent className="p-4">
            <p className="text-text2 text-sm">
              <strong>Note:</strong> Where we rely on consent, you have the right to withdraw it at any
              time without affecting the lawfulness of processing carried out prior to withdrawal.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">6. Third-Party Cookies and Data Sharing</h2>
        <p>
          Some cookies on our Platform are set by third-party service providers. These parties process
          data under their own privacy policies:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>
            <strong className="text-text">Cloudflare, Inc.:</strong>{' '}
            <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
              https://www.cloudflare.com/privacypolicy/
            </a>
          </li>
          <li>
            <strong className="text-text">Amplitude, Inc.:</strong>{' '}
            <a href="https://amplitude.com/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
              https://amplitude.com/privacy
            </a>
          </li>
        </ul>
        <p className="mt-4">
          Where these providers process personal data on our behalf, we have Data Processing Agreements
          (DPAs) in place in accordance with GDPR Article 28 and POPIA Section 21. We do not sell
          personal data collected through cookies to third parties.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">7. International Data Transfers</h2>
        <p>
          Some of our third-party cookie providers are located outside the European Economic Area (EEA)
          and outside South Africa. Where personal data is transferred internationally, we ensure
          appropriate safeguards are in place:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Standard Contractual Clauses (SCCs) approved by the European Commission (for GDPR).</li>
          <li>Adequacy decisions where the destination country provides equivalent protection.</li>
          <li>Binding Corporate Rules (BCRs) where applicable.</li>
          <li>Authorisation from the Information Regulator of South Africa where required under POPIA Section 72.</li>
        </ul>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">8. Your Rights Under GDPR (EEA &amp; UK)</h2>
        <p>
          If you are located in the European Economic Area (EEA) or the United Kingdom, you have the
          following rights:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">Right to be Informed:</strong> To receive clear information about how we use your data.</li>
          <li><strong className="text-text">Right of Access:</strong> To request a copy of personal data we hold about you.</li>
          <li><strong className="text-text">Right to Rectification:</strong> To request correction of inaccurate personal data.</li>
          <li><strong className="text-text">Right to Erasure:</strong> To request deletion of your personal data in certain circumstances.</li>
          <li><strong className="text-text">Right to Restrict Processing:</strong> To ask us to limit how we process your data.</li>
          <li><strong className="text-text">Right to Data Portability:</strong> To receive your data in a structured, machine-readable format.</li>
          <li><strong className="text-text">Right to Object:</strong> To object to processing based on legitimate interests or for direct marketing.</li>
          <li><strong className="text-text">Right to Withdraw Consent:</strong> To withdraw cookie consent at any time via our consent manager.</li>
          <li><strong className="text-text">Right to Lodge a Complaint:</strong> With your local Data Protection Authority (e.g., the ICO in the UK).</li>
        </ul>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">9. Your Rights Under POPIA (South Africa)</h2>
        <p>
          If you are located in South Africa, the Protection of Personal Information Act 4 of 2013
          grants you the following rights:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">Right to Access</strong> (s. 23): To request access to personal information we hold about you.</li>
          <li><strong className="text-text">Right to Correction or Deletion</strong> (s. 24): To request that we correct or delete inaccurate, irrelevant, or outdated personal information.</li>
          <li><strong className="text-text">Right to Object to Processing</strong> (s. 11(3)): To object on reasonable grounds to the processing of your personal information.</li>
          <li><strong className="text-text">Right to Object to Direct Marketing</strong> (s. 69): To opt out of direct marketing communications at any time.</li>
          <li><strong className="text-text">Right Not to be Subject to Automated Decision-Making</strong> (s. 71): To request human review of decisions made solely by automated means that significantly affect you.</li>
          <li><strong className="text-text">Right to Lodge a Complaint:</strong> With the Information Regulator of South Africa.</li>
        </ul>
        <Card className="border-border/30 bg-surface2 mt-4">
          <CardContent className="p-4 space-y-1 text-sm">
            <p className="font-semibold text-text">Information Regulator of South Africa</p>
            <p>
              Website:{' '}
              <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                https://www.justice.gov.za/inforeg/
              </a>
            </p>
            <p>
              Email:{' '}
              <a href="mailto:inforeg@justice.gov.za" className="text-brand hover:underline">
                inforeg@justice.gov.za
              </a>
            </p>
            <p>Tel: +27 (0)10 023 5207</p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">10. Managing and Withdrawing Cookie Consent</h2>
        <p>
          If you do not wish to accept cookies, you can manage your preferences at any time by
          configuring your browser to refuse cookies from our website. Most browsers accept cookies by
          default.
        </p>
        <p className="mt-4">
          If you access our Site from multiple devices, you will need to update your cookie preferences
          on each device separately. Disabling certain cookies may limit your ability to access some
          features and content on our Site.
        </p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">10.1 Browser Controls</h3>
        <ul className="list-disc list-inside space-y-2">
          <li><strong className="text-text">Google Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies and other site data.</li>
          <li><strong className="text-text">Mozilla Firefox:</strong> Options &gt; Privacy &amp; Security &gt; Cookies and Site Data.</li>
          <li><strong className="text-text">Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data.</li>
          <li><strong className="text-text">Microsoft Edge:</strong> Settings &gt; Cookies and Site Permissions.</li>
          <li><strong className="text-text">Opera:</strong> Settings &gt; Advanced &gt; Privacy &amp; Security &gt; Cookies.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">10.2 Opt-Out Tools</h3>
        <p>
          To opt out of Amplitude analytics tracking, contact us at the details in Section 13 to request
          exclusion, or block the Amplitude domain through your browser's content settings. Cloudflare
          security cookies cannot be disabled without affecting Platform functionality.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">11. Cookie Retention Periods</h2>
        <p>
          We retain data collected through cookies only for as long as necessary to fulfil the purposes
          described in this Policy. All three cookies currently used on our Site are session cookies,
          meaning they expire automatically when you close your browser or your session ends:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">__cf_bm (Cloudflare):</strong> expires approximately 30 minutes after your session begins.</li>
          <li><strong className="text-text">_cfuvid (Cloudflare):</strong> expires at the end of your browser session.</li>
          <li><strong className="text-text">AMP_0c48a381e1 (Amplitude):</strong> expires at the end of your browser session.</li>
        </ul>
        <p className="mt-4">
          You may delete cookies at any time through your browser settings. This will not affect the
          lawfulness of processing that occurred before deletion.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">12. Updates to This Policy</h2>
        <p>
          We review and update this Cookies Policy periodically to reflect changes in technology, legal
          requirements, our services, or our data practices. The "Last Updated" date at the top of this
          document indicates when the most recent revision was made.
        </p>
        <p className="mt-4">
          Where changes are material, we will notify registered users through email or an in-app
          notification and, where required by law, seek renewed consent. We encourage you to review this
          Policy regularly.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">13. Contact Us</h2>
        <p>For questions, requests, or concerns about this Cookies Policy or how we handle your personal data:</p>
        <Card className="border-border/30 bg-surface2 mt-4">
          <CardContent className="p-6 space-y-2 text-sm">
            <p><strong className="text-text">Contact:</strong> Nucleus Systems (Pty) Ltd</p>
            <p>43 Silver Creek Eco Park Estates</p>
            <p><strong className="text-text">Tel:</strong> 083 680 3427</p>
            <p>
              <strong className="text-text">Email:</strong>{' '}
              <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
                godfrey.kutumela@nucleus-systems.com
              </a>
            </p>
            <p>
              <strong className="text-text">Web:</strong>{' '}
              <a href="https://www.nucleus-systems.com" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                www.nucleus-systems.com
              </a>
            </p>
            <p><strong className="text-text">Web Developer:</strong> Godfrey Kutumela</p>
            <p className="pt-2 text-muted">For POPIA requests, include the subject line: "POPIA Request."</p>
          </CardContent>
        </Card>
      </section>
    </LegalPageLayout>
  );
};

export default CookiePolicy;
